/** English content of the chauffeur home (translation of HUB_FR). */
import type { ChauffeurHubData } from "./hubDict";

export const HUB_EN: ChauffeurHubData = {
  hero: {
    eyebrow: "Private chauffeur in Cannes",
    title: "Private chauffeur & VTC in Cannes",
    subtitle:
      "Premium travel on the French Riviera: transfers, events, business.",
    ctaPrimary: { href: "/reservation", label: "Book" },
    ctaSecondary: { href: "/contact", label: "Request a quote" },
    trustLine:
      "Instant confirmation • Secure payment • Reply in under a minute",
    chips: [
      "Airport/hotel welcome",
      "Event coordination",
      "Dedicated human support",
    ],
    bookingTitle: "Book in 3 steps",
    steps: [
      "Choose the date and time of pick-up",
      "Tell us the route or need (transfer, hourly hire)",
      "Instant validation",
    ],
    bookingCta: "Book now",
    bookingNote: "No payment required to start",
  },
  services: {
    eyebrow: "Private chauffeur services",
    heading: "High-end chauffeur services",
    intro:
      "Premium services designed for your needs in Cannes and across the whole French Riviera, with punctuality, comfort and discretion.",
    mostRequested: "Most requested service",
    priorityBadge: "Priority on punctuality and coordination",
    points: ["Fast reply", "Premium welcome", "24/7 service"],
    items: [
      {
        title: "Airport transfers",
        text: "Optimised arrivals/departures with flight tracking and precise schedule coordination.",
        icon: "ph:airplane-tilt",
      },
      {
        title: "Hourly hire",
        text: "Private chauffeur by the hour, half-day or full day to fit your schedule.",
        icon: "ph:clock",
      },
      {
        title: "Events",
        text: "VIP set-ups, shuttles and flow management across Cannes, Monaco and Nice.",
        icon: "ph:calendar-check",
      },
      {
        title: "Business",
        text: "Roadshows, business meetings and corporate travel in complete discretion.",
        icon: "ph:briefcase",
      },
      {
        title: "Weddings",
        text: "Pinpoint ceremony/reception routing with premium service for guests.",
        icon: "ph:heart",
      },
      {
        title: "Excursions",
        text: "Scenic routes and private itineraries on the French and Italian Riviera.",
        icon: "ph:mountains",
      },
    ],
  },
  indicators: {
    heading: "Service indicators and the KS Rent Services promise",
    items: [
      {
        label: "Confirmation",
        value: "Fast",
        text: "priority reply on qualified requests",
      },
      {
        label: "Availability",
        value: "7 days a week",
        text: "anticipated pick-up and extended hours",
      },
      {
        label: "Active area",
        value: "Cannes - Monaco - Nice",
        text: "Italian Riviera extensions on request",
      },
      {
        label: "Positioning",
        value: "Discreet premium",
        text: "understated, smooth and punctual service",
      },
    ],
  },
  motion: {
    eyebrow: "Operational commitment",
    heading: "Our service method on the French Riviera",
    text: "At KS Rent Services, every assignment is framed with a single point of contact, clear schedule tracking and reliable on-the-ground execution from Cannes to Monaco, Nice and Sophia.",
    badge: "Premium chauffeur service",
    signatureText:
      "Private chauffeur in Cannes and on the French Riviera: transfers, business, events and hourly hire with a consistent level of service.",
    leftAlt: "Premium passenger experience",
    rightAlt: "Airport and heliport transfers",
  },
  locations: {
    eyebrow: "Chauffeur territorial coverage",
    heading: "Areas served and preferred routes",
    introPrefix:
      "Private chauffeur service based in Cannes, serving the whole French Riviera up to Monaco. ",
    srPrefix: "Areas served: ",
    corridorsTitle: "Priority corridors",
    corridorNote: "Flexible departure - structured confirmation",
    corridorPrefix: "Cannes -> ",
    items: [
      {
        name: "Cannes",
        description:
          "operational base and fast deployment for any local request",
      },
      {
        name: "Nice",
        description: "city centre, airport and business districts",
      },
      {
        name: "Antibes",
        description: "old town, Juan-les-Pins and the events district",
      },
      {
        name: "Monaco",
        description: "principality, hotels and corporate venues",
      },
      {
        name: "Mandelieu",
        description: "residence, marina and business-meeting connections",
      },
      { name: "Grasse", description: "business trips and private travel" },
      {
        name: "Saint-Tropez",
        description: "seasonal and weekend premium transfers",
      },
      {
        name: "Menton",
        description: "long-distance travel on the eastern French Riviera",
      },
      {
        name: "Milan",
        description: "on request for executive needs and events",
      },
      {
        name: "Italian Riviera",
        description: "a consistent extension of your main itinerary",
      },
    ],
  },
  process: {
    heading: "The chauffeur process in 4 clear steps",
    intro:
      "A simple method to avoid back-and-forth: you state the need, we frame the service, we confirm, we execute.",
    stepWord: "Step",
    steps: [
      "Qualify the need: route, timing, priorities, number of passengers.",
      "Service proposal: format, duration, options and on-the-ground constraints.",
      "Written confirmation: meeting point, logistics reminder and instructions.",
      "Execution: active tracking through to the final destination.",
    ],
    leftAlt: "Premium chauffeur coordination",
    rightAlt: "Passenger welcome and schedule tracking",
  },
  seoContent: {
    eyebrow: "Useful chauffeur information",
    heading:
      "Private chauffeur in Cannes: useful content to compare and decide",
    paragraphs: [
      "Our private chauffeur service in Cannes meets business, event and private needs with a stable level of service: punctuality, smoothness, discretion.",
      "You get a clear solution before departure: service format, time frame, useful options and a level of support adapted to the context.",
      "The goal is not just to transport you, but to protect your schedule, your image and your passenger comfort on every segment of the trip.",
    ],
    bullets: [
      "7-day-a-week pick-up with structured confirmation",
      "Experienced chauffeurs, premium and discreet manner",
      "Customisable routes without complicating the booking",
      "Clear pricing and proactive communication",
    ],
    metrics: [
      { k: "Average reply", v: "< 15 min" },
      { k: "Availability", v: "7 days a week" },
      { k: "Active area", v: "Cannes - Monaco - Nice" },
    ],
    whyTitle: "Why choose us",
    whyItems: [
      "Experienced and discreet chauffeurs",
      "Maintained premium vehicles",
      "Simple booking and fast confirmation",
      "24/7 service departing from Cannes",
    ],
  },
  atouts: {
    eyebrow: "Cross-cutting strengths",
    heading: "The strong points carried over from our chauffeur services",
    intro:
      "Concrete strengths used across our airport, events, corporate, Cannes, French Riviera and services pages.",
    items: [
      {
        title: "Flight tracking",
        text: "Real-time adjustment on sensitive arrivals.",
      },
      {
        title: "Project lead",
        text: "Dedicated coordination for your event operations.",
      },
      {
        title: "SLA & reliability",
        text: "A clear service framework for corporate needs.",
      },
      {
        title: "Event access",
        text: "Smooth steering for congresses, trade shows and festivals.",
      },
      {
        title: "Local knowledge",
        text: "Routes adapted across the whole French Riviera.",
      },
      {
        title: "24/7 punctuality",
        text: "Consistent execution on priority requests.",
      },
    ],
  },
  faq: {
    eyebrow: "Frequently asked questions",
    heading: "Private chauffeur FAQ",
    items: [
      {
        q: "When is the right time to book a private chauffeur?",
        a: "As early as possible for busy periods. For an urgent request, send your exact timing and we'll quickly confirm feasibility.",
      },
      {
        q: "Is hourly hire suited to business days?",
        a: "Yes. It's the most flexible format for chaining meetings, a variable itinerary and adjustments during the day.",
      },
      {
        q: "Can you handle an event with several pick-up points?",
        a: "Yes, we can organise a multi-point pick-up plan with operational coordination to keep a clear passenger flow.",
      },
      {
        q: "What's the difference between a regular taxi and this chauffeur service?",
        a: "The chauffeur service is structured around your overall journey, with a higher level of personalisation and tracking.",
      },
    ],
    asideEyebrow: "Fast reply",
    asideTitle: "Need a quick validation before booking?",
    asideText:
      "Our team can confirm feasibility, the service scope and the pick-up framework on your priority requests.",
    asideBullets: [
      "7-day-a-week support",
      "FR / EN welcome",
      "Event & airport coordination",
    ],
  },
  fleet: {
    eyebrow: "Our fleet",
    heading: "A page for each vehicle in our chauffeur fleet",
    intro:
      "Choose the vehicle suited to your trip: business sedan, prestige sedan, 7-seat van, luxury SUV, VIP minibus or armored vehicle.",
    links: [
      {
        label: "Sedan with chauffeur (E-Class)",
        href: "/en/chauffeur/sedan-with-chauffeur",
      },
      {
        label: "Mercedes S-Class with chauffeur",
        href: "/en/chauffeur/s-class-with-chauffeur",
      },
      {
        label: "Van with chauffeur — 7 seats",
        href: "/en/chauffeur/van-with-chauffeur",
      },
      {
        label: "Range Rover with chauffeur — SUV",
        href: "/en/chauffeur/suv-with-chauffeur",
      },
      {
        label: "VIP minibus — up to 16 seats",
        href: "/en/chauffeur/vip-minibus",
      },
      {
        label: "Security chauffeur & armored vehicle",
        href: "/en/chauffeur/security-chauffeur",
      },
    ],
  },
  complementary: {
    heading: "Complementary services",
    intro:
      "Find the useful pages to quickly compare chauffeur services, private excursions and vehicle rental.",
    links: [
      {
        label: "Private excursions on the French Riviera",
        href: "/excursions",
      },
      { label: "Premium vehicle rental", href: "/location/vehicules" },
      {
        label: "Airport transfer chauffeur",
        href: "/en/chauffeur/transfers",
      },
      {
        label: "Wedding car in Cannes",
        href: "/en/chauffeur/wedding-car-cannes",
      },
      { label: "Book your trip online", href: "/reservation" },
    ],
  },
  seo: {
    title:
      "Private chauffeur in Cannes & French Riviera VTC | KS Rent Services",
    description:
      "Private chauffeur and VTC in Cannes: airport transfers, events, business and hourly hire across the French Riviera. Simple booking, 7-day-a-week service.",
    canonical: "/en/chauffeur",
  },
};
